import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Button, Modal, Row, Col } from 'react-bootstrap';
import { FaPlus, FaFileAlt, FaClock } from 'react-icons/fa';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import TableWrapper from '../../../components/TableWrapper';
import FilterSelect from '../../../components/FilterSelect';
import DocumentForm from '../DocumentForm';
import { useColumnFilter } from '../../../hooks/useColumnFilter';
import PeopleService from '../PeopleService';
import { FORMATO_FECHA } from '../../../utils/constants';

dayjs.extend(utc);

// ✅ AGREGADA PROP isPersonDeactivated
const DocumentsSection = ({ personId, documents = [], onDataChange, showSnackbar, isPersonDeactivated = false }) => {
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [filteredDocuments, setFilteredDocuments] = useState(documents);

  const formatDateSafely = useCallback((dateValue) => {
    if (!dateValue) return '';
    
    try {
      if (dateValue.includes('T') && dateValue.includes('Z')) {
        return dayjs.utc(dateValue).format(FORMATO_FECHA);
      }
      return dayjs(dateValue).format(FORMATO_FECHA);
    } catch (error) {
      console.error('Error al formatear fecha:', dateValue, error);
      return '';
    }
  }, []);

  const expirationStatusGetter = useCallback((doc) => {
    if (!doc.endDate) return 'Sin fecha';
    
    try {
      const today = dayjs().startOf('day');
      
      let endDate;
      if (doc.endDate.includes('T') && doc.endDate.includes('Z')) {
        endDate = dayjs.utc(doc.endDate).startOf('day');
      } else {
        endDate = dayjs(doc.endDate).startOf('day');
      }
      
      const daysUntilExpiration = endDate.diff(today, 'day');
      
      if (daysUntilExpiration < 0) return 'Vencido';
      if (daysUntilExpiration <= 30) return 'Por vencer';
      return 'Vigente';
    } catch (error) {
      console.error('Error al calcular estado de vencimiento:', doc.endDate, error);
      return 'Error';
    }
  }, []);

  const { 
    filterOptions: nameOptions, 
    activeFilters: activeNames,
    handleOptionsUpdate: handleNameOptionsUpdate,
    handleFiltersChange: handleNameChange,
    filterData: filterByName 
  } = useColumnFilter(documents, 'name', null, `person-${personId}-documents`);

  const { 
    filterOptions: statusOptions, 
    activeFilters: activeStatuses,
    handleOptionsUpdate: handleStatusOptionsUpdate,
    handleFiltersChange: handleStatusChange,
    filterData: filterByStatus 
  } = useColumnFilter(documents, 'endDate', expirationStatusGetter, `person-${personId}-documents`);

  useEffect(() => {
    let result = [...documents];
    
    if (activeNames && activeNames.length > 0) {
      result = filterByName(result);
    }
    
    if (activeStatuses && activeStatuses.length > 0) {
      result = filterByStatus(result);
    }
    
    setFilteredDocuments(result);
  }, [documents, activeNames, activeStatuses, filterByName, filterByStatus]);

  const textFilterParams = useMemo(() => ({
    debounceMs: 200,
    suppressAndOrCondition: true
  }), []);

  const handleViewDocument = async (doc) => {
    if (!doc || !doc.id) {
      if (showSnackbar) {
        showSnackbar('error', 'ID de documento no válido');
      }
      return;
    }
    
    try {
      const result = await PeopleService.getDocument(doc.id, false, doc.name); 
      
      if (result && result.success) {
        const actionText = result.action === 'viewed' ? 'abierto' : 'descargado';
        if (showSnackbar) {
          showSnackbar('success', `Documento ${actionText} correctamente: ${result.filename}`);
        }
      } else {
        throw new Error(result?.error || 'Error desconocido al procesar el documento');
      }
    } catch (error) {
      console.error('Error al procesar documento:', error);
      if (showSnackbar) {
        showSnackbar('error', `Error al procesar el documento: ${error.message}`);
      }
    }
  };

  const handleDownloadDocument = async (doc) => {
    if (!doc || !doc.id) {
      if (showSnackbar) {
        showSnackbar('error', 'ID de documento no válido');
      }
      return;
    }
    
    try {
      const result = await PeopleService.getDocument(doc.id, true, doc.name);
      
      if (result && result.success) {
        if (showSnackbar) {
          showSnackbar('success', `Documento descargado correctamente: ${result.filename}`);
        }
      } else {
        throw new Error(result?.error || 'Error desconocido al descargar el documento');
      }
    } catch (error) {
      console.error('Error al descargar documento:', error);
      if (showSnackbar) {
        showSnackbar('error', `Error al descargar el documento: ${error.message}`);
      }
    }
  };

  const documentColumns = useMemo(() => [
    { 
      headerName: 'Nombre',
      field: 'name',
      flex: 1,
      filter: 'agTextColumnFilter', 
      floatingFilter: true,
      filterParams: textFilterParams
    },
    { 
      headerName: 'Vencimiento', 
      field: 'endDate', 
      width: 130,
      valueFormatter: params => formatDateSafely(params.value),
      filter: 'agTextColumnFilter',
      filterValueGetter: params => {  
        return formatDateSafely(params.data.endDate);
      },
      floatingFilter: true,
      filterParams: textFilterParams
    },
    {
      headerName: 'Estado',
      field: 'endDate',
      width: 100,
      valueGetter: params => expirationStatusGetter(params.data),
      cellRenderer: params => {
        const status = expirationStatusGetter(params.data);
        return `${status}`;
      },
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Observaciones', 
      field: 'observations',
      flex: 1.5,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams
    }
  ], [textFilterParams, expirationStatusGetter, formatDateSafely]);

  // ✅ VALIDACIÓN AGREGADA
  const handleNewDocument = () => {
    if (isPersonDeactivated) {
      showSnackbar('warning', 'No se pueden agregar documentos a una persona dada de baja');
      return;
    }
    setSelectedDocument(null);
    setShowDocumentModal(true);
  };

  // ✅ VALIDACIÓN AGREGADA
  const handleEditDocument = (document) => {
    if (isPersonDeactivated) {
      showSnackbar('warning', 'No se pueden editar documentos de una persona dada de baja');
      return;
    }
    setSelectedDocument(document);
    setShowDocumentModal(true);
  };

  const handleCloseDocumentModal = () => {
    setShowDocumentModal(false);
    setSelectedDocument(null);
  };

  const handleDocumentSaved = () => {
    setShowDocumentModal(false);
    if (onDataChange) onDataChange();
    
    if (showSnackbar) {
      showSnackbar(
        'success', 
        selectedDocument ? 'Documento actualizado correctamente' : 'Documento creado correctamente'
      );
    }
  };

  // ✅ VALIDACIÓN AGREGADA
  const handleDeleteDocument = async (document) => {
    if (isPersonDeactivated) {
      showSnackbar('warning', 'No se pueden eliminar documentos de una persona dada de baja');
      return;
    }
    
    if (window.confirm(`¿Está seguro que desea eliminar el documento "${document.name}"?`)) {
      try {
        await PeopleService.deleteDocument(document.id);
        
        if (showSnackbar) {
          showSnackbar('success', 'Documento eliminado correctamente');
        }
        
        if (onDataChange) onDataChange();
      } catch (error) {
        console.error('Error al eliminar documento:', error);
        if (showSnackbar) {
          showSnackbar('error', 'Error al eliminar el documento');
        }
      }
    }
  };

  return (
    <div className="d-flex flex-column h-100">
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex gap-2">
              <FilterSelect
                options={nameOptions}
                activeFilters={activeNames}
                onFiltersChange={handleNameChange}
                onOptionsUpdate={handleNameOptionsUpdate}
                title="Filtrar por documento"
                filterKey={`person-${personId}-documents-names`}
                icon={<FaFileAlt />}
                variant="outline-secondary"
              />
              
              <FilterSelect
                options={statusOptions}
                activeFilters={activeStatuses}
                onFiltersChange={handleStatusChange}
                onOptionsUpdate={handleStatusOptionsUpdate}
                title="Filtrar por estado"
                filterKey={`person-${personId}-documents-status`}
                icon={<FaClock />}
                variant="outline-secondary"
              />
            </div>
            
            {/* ✅ BOTÓN DESHABILITADO SI ESTÁ DADA DE BAJA */}
            <Button 
              variant="primary" 
              size="sm"
              className="d-flex align-items-center"
              onClick={handleNewDocument}
              disabled={isPersonDeactivated}
            >
              <FaPlus className="me-1" /> Subir Documento
            </Button>
          </div>
        </Col>
      </Row>
      
      <div className="flex-grow-1" style={{ height: 'calc(100vh - 280px)' }}>
        <TableWrapper 
          columnDefs={documentColumns}
          rowData={filteredDocuments}
          tableType="documentos"
          onView={handleViewDocument}
          onEdit={handleEditDocument}
          onDelete={handleDeleteDocument}
          onDownload={handleDownloadDocument}
          customGridOptions={{
            pagination: true,
            paginationPageSize: 20,
            domLayout: 'autoHeight'
          }}
        />
      </div>
      
      <Modal 
        show={showDocumentModal} 
        onHide={handleCloseDocumentModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className='text-center w-100'>
            {selectedDocument ? 'Editar Documento' : 'Subir Nuevo Documento'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showDocumentModal && (
            <DocumentForm 
              personId={personId}
              document={selectedDocument}
              onSave={handleDocumentSaved}
              onCancel={handleCloseDocumentModal}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DocumentsSection;